/**
 * Send To All Modal
 *
 * Purpose: Modal for sending notification to all users (with user type selection)
 */

import { useState } from 'react'
import { X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { LoadingSpinner } from '@/features/common/components/LoadingSpinner'

interface SendToAllModalProps {
  isOpen: boolean
  onClose: () => void
  onSend: (userType: string, title: string, message: string) => Promise<void>
  isLoading?: boolean
}

export function SendToAllModal({
  isOpen,
  onClose,
  onSend,
  isLoading = false,
}: SendToAllModalProps) {
  const [userType, setUserType] = useState('')
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [errors, setErrors] = useState<{
    userType?: string
    title?: string
    message?: string
  }>({})

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Reset errors
    const newErrors: { userType?: string; title?: string; message?: string } = {}

    // Validate
    if (!userType || userType === '--Select--') {
      newErrors.userType = 'User type is required'
    }
    if (!title.trim()) {
      newErrors.title = 'Title is required'
    }
    if (!message.trim()) {
      newErrors.message = 'Message is required'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    try {
      await onSend(userType, title.trim(), message.trim())
      // Reset form on success
      setUserType('')
      setTitle('')
      setMessage('')
      setErrors({})
      onClose()
    } catch (error) {
      // Error handling is done in parent component
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setUserType('')
      setTitle('')
      setMessage('')
      setErrors({})
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={handleClose}
          />

          {/* Dialog */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Send Notification To All</h3>
                <button
                  onClick={handleClose}
                  className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                  disabled={isLoading}
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* User Type */}
                <div>
                  <label htmlFor="userType" className="block text-sm font-medium text-gray-700 mb-2">
                    Select User Type<span className="text-red-500">*</span>
                  </label>
                  <select
                    id="userType"
                    value={userType}
                    onChange={e => {
                      setUserType(e.target.value)
                      setErrors(prev => ({ ...prev, userType: undefined }))
                    }}
                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      errors.userType ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isLoading}
                  >
                    <option value="--Select--">--Select--</option>
                    <option value="All">All</option>
                    <option value="Android">Android</option>
                    <option value="IOS">IOS</option>
                  </select>
                  {errors.userType && (
                    <p className="mt-1 text-sm text-red-600">{errors.userType}</p>
                  )}
                </div>

                {/* Title */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                    Title<span className="text-red-500">*</span>
                  </label>
                  <input
                    id="title"
                    type="text"
                    value={title}
                    onChange={e => {
                      setTitle(e.target.value)
                      setErrors(prev => ({ ...prev, title: undefined }))
                    }}
                    placeholder="Enter notification title"
                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      errors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isLoading}
                  />
                  {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message<span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={e => {
                      setMessage(e.target.value)
                      setErrors(prev => ({ ...prev, message: undefined }))
                    }}
                    placeholder="Enter notification message"
                    rows={4}
                    className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 resize-y ${
                      errors.message ? 'border-red-500' : 'border-gray-300'
                    }`}
                    disabled={isLoading}
                  />
                  {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message}</p>}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isLoading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    style={{ backgroundColor: '#019b9a' }}
                  >
                    {isLoading ? (
                      <>
                        <LoadingSpinner size="sm" />
                        Sending...
                      </>
                    ) : (
                      'Send'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}

